import React, { useMemo, useState } from "react";
import useApi from "../useApi";
import useGlobalSummary from "../useGlobalSummary";
import { getApiPaths } from "../constants/healthEndpoints";
import {
  StoreIcon,
  WarningIcon,
  HeartIcon,
  AlertBellIcon,
  HealthOperationsIcon
} from "../utils/healthUtils";

/**
 * DashboardMetrics Component
 * Displays KPI cards for both Global and Market views
 */
// Titles only, so the placeholder grid is the same shape as the loaded one.
const GLOBAL_CARD_TITLES = [
  'GLOBAL HEALTH',
  'TOTAL STORES',
  'STORES WITH ISSUES',
  'CRITICAL STORES',
  'CRITICAL ALERTS',
  'SERVICES HEALTHY',
];

// The market row is titled per market, but is otherwise the same six cards.
const marketCardTitles = (marketCode) => [
  `${marketCode} Health`,
  'Total Stores',
  'Stores with Issues',
  'Critical Stores',
  'Critical Alerts',
  'Services Healthy',
];

/**
 * Metric cards will each carry an AI-derived insight. The analysis is not built
 * yet, so the affordance is real but the content is explicitly a placeholder —
 * an invented "insight" on an operations dashboard would be read as guidance.
 */
const INSIGHTS_READY = false;

const DashboardMetrics = ({ mode, marketCode, accountId }) => {
  const [openInsight, setOpenInsight] = useState(null);
  console.log(`[DashboardMetrics] Mode: ${mode}, Market: ${marketCode}, AccountId: ${accountId}`);

  // Get API endpoints based on mode
  const apiPaths = useMemo(() => {
    if (mode === 'market' && accountId) {
      return getApiPaths(accountId);
    }
    return null;
  }, [mode, accountId]);

  // Fetch stores data
  const {
    data: storesData = [],
    loading: storesLoading,
    error: storesError
  } = useApi(apiPaths?.STORES_VIEW);

  // Fetch services data
  const {
    data: servicesData = {},
    loading: servicesLoading,
    error: servicesError
  } = useApi(apiPaths?.SERVICES_VIEW);

  // Only the global view needs the roll-up; the hook no-ops cheaply otherwise.
  const {
    data: globalSummary,
    loading: globalLoading,
    error: globalError,
  } = useGlobalSummary();

  // Calculate metrics
  const metrics = useMemo(() => {
    if (mode === 'global') {
      // Aggregated across every live market by the proxy. Null until it
      // arrives, and null if it fails — the placeholder figures that used to
      // live here read as real data and were wrong by an order of magnitude.
      const summary = globalSummary?.global;
      if (!summary) return null;

      const { stores, unhealthy, critical, healthPercent } = summary;
      const pct = (n) => (stores ? Number(((100 * n) / stores).toFixed(1)) : 0);

      // A service counts as impacted if any market reports stores affected.
      const services = (globalSummary.markets || []).reduce((acc, market) => {
        Object.entries(market.services || {}).forEach(([name, count]) => {
          acc[name] = (acc[name] || 0) + count;
        });
        return acc;
      }, {});
      const serviceNames = Object.keys(services);
      const healthyServices = serviceNames.filter((n) => services[n] === 0).length;

      return {
        healthPercent,
        totalStores: stores,
        storeChange: 0,
        unhealthyStores: unhealthy,
        unhealthyPercent: pct(unhealthy),
        unhealthyChange: 0,
        unhealthyChangePercent: 0,
        criticalStores: critical,
        criticalPercent: pct(critical),
        criticalChange: 0,
        criticalChangePercent: 0,
        // Distinct systemic conditions, not a per-store tally: one upstream
        // failure attributed to 1,500 stores is one alert, not 1,500.
        criticalAlerts: (globalSummary.markets || []).reduce(
          (n, m) => n + (m.systemic?.length || 0),
          0
        ),
        servicesHealthy: healthyServices,
        servicesTotal: serviceNames.length,
        servicesCoverage: serviceNames.length
          ? Math.round((100 * healthyServices) / serviceNames.length)
          : 0,
        incomplete: !summary.complete,
        marketsReporting: summary.marketsReporting,
        marketsConfigured: summary.marketsConfigured,
      };
    }

    // Market-specific metrics
    if (storesLoading || servicesLoading || !storesData) {
      return null;
    }

    const totalStores = storesData.length || 0;
    const healthyStores = storesData.filter(s => 
      s.status === 'healthy' || s.color === 'green'
    ).length;
    const unhealthyStores = storesData.filter(s => 
      s.status === 'down' || s.color === 'red' || s.color === 'amber'
    ).length;
    const criticalStores = storesData.filter(s => 
      s.color === 'red'
    ).length;
    
    const healthPercent = totalStores > 0 
      ? Math.round((healthyStores / totalStores) * 100) 
      : 0;

    const unhealthyPercent = totalStores > 0
      ? ((unhealthyStores / totalStores) * 100).toFixed(1)
      : 0;

    const criticalPercent = totalStores > 0
      ? ((criticalStores / totalStores) * 100).toFixed(1)
      : 0;

    // Count healthy services
    const servicesArray = Object.values(servicesData || {});
    const healthyServices = servicesArray.filter(s => s.percent >= 95).length;
    const totalServices = servicesArray.length || 5;
    const servicesCoverage = totalServices > 0
      ? Math.round((healthyServices / totalServices) * 100)
      : 0;

    return {
      healthPercent,
      totalStores,
      storeChange: 0, // You can calculate from historical data
      unhealthyStores,
      unhealthyPercent,
      unhealthyChange: 0,
      unhealthyChangePercent: 0,
      criticalStores,
      criticalPercent,
      criticalChange: 0,
      criticalChangePercent: 0,
      criticalAlerts: 0, // You can fetch from alerts API
      servicesHealthy: healthyServices,
      servicesTotal: totalServices,
      servicesCoverage,
    };
  }, [mode, storesData, servicesData, storesLoading, servicesLoading, globalSummary]);

  // Loading and failure both render the real grid with the real card markup, so
  // the CSS gives them exactly the height the loaded state will have. A
  // spinner block or a one-line error was 16px and 58px shorter respectively,
  // and everything below it jumped when the data landed.
  if (mode === 'global' && (globalLoading || globalError || !metrics)) {
    const note = globalLoading
      ? null
      : `Unavailable${globalError ? `: ${globalError.message}` : ""}`;
    return (
      <div className="global-metrics-grid" aria-busy={globalLoading ? "true" : undefined}>
        {GLOBAL_CARD_TITLES.map((title) => (
          <div className="global-metric-card" key={title}>
            <div className="global-metric-card-header">
              <span className="global-metric-label">{title}</span>
              <div className="global-metric-icon" />
            </div>
            <div className="global-value-row">
              <span className="global-metric-value">—</span>
            </div>
            <div className="global-metric-subtext">
              {note || "Aggregating markets..."}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // The market row gets the same treatment. It used to swap in a spinner
  // block that was taller than the grid, so the row grew on entry and shrank
  // again when the APIs answered. useApi starts at loading=false with
  // data=null, so "no data yet" has to count as pending too — otherwise the
  // first frame falls through to the null return below and the row is 0px.
  if (mode === 'market') {
    const failure = storesError || servicesError;
    const pending =
      !failure &&
      (storesLoading || servicesLoading || !storesData || !servicesData);

    if (pending || failure || !metrics) {
      const note = pending
        ? 'Loading...'
        : `Unavailable${failure ? `: ${failure.message}` : ''}`;
      return (
        <div className="global-metrics-grid" aria-busy={pending ? "true" : undefined}>
          {marketCardTitles(marketCode).map((title) => (
            <div className="global-metric-card" key={title}>
              <div className="global-metric-card-header">
                <span className="global-metric-label">{title}</span>
                <div className="global-metric-icon" />
              </div>
              <div className="global-value-row">
                <span className="global-metric-value">—</span>
              </div>
              <div className="global-metric-subtext">{note}</div>
            </div>
          ))}
        </div>
      );
    }
  }

  // No metrics available
  if (!metrics) {
    return null;
  }

  // Define metric cards with smaller icons
  const metricCards = [
    {
      title: mode === 'global' ? 'Global Health' : `${marketCode} Health`,
      value: `${metrics.healthPercent}%`,
      change: "2%",
      subtitle: "vs previous 30 min",
      trend: true,
      type: "success",
      icon: <HeartIcon size={20} color="#68b561" />
    },
    {
      title: "Total Stores",
      value: metrics.totalStores.toLocaleString(),
      change: metrics.storeChange > 0 ? `↑ ${metrics.storeChange}` : null,
      subtitle: "vs previous 30 min",
      type: "success",
      icon: <StoreIcon size={20} color="#6b7280" />
    },
    {
      title: "Stores with Issues",
      value: metrics.unhealthyStores.toLocaleString(),
      extraValue: `(${metrics.unhealthyPercent}%)`,
      change: metrics.unhealthyChange > 0 
        ? `↑ ${metrics.unhealthyChange} (${metrics.unhealthyChangePercent}%)` 
        : null,
      subtitle: "vs previous 30 min",
      type: "danger",
      icon: <WarningIcon size={20} color="#eab308" />
    },
    {
      title: "Critical Stores",
      value: metrics.criticalStores.toLocaleString(),
      extraValue: `(${metrics.criticalPercent}%)`,
      change: metrics.criticalChange > 0
        ? `↑ ${metrics.criticalChange} (${metrics.criticalChangePercent}%)`
        : null,
      subtitle: "vs previous 30 min",
      type: "danger",
      icon: <StoreIcon size={20} color="#ef4444" />
    },
    {
      title: "Critical Alerts",
      value: metrics.criticalAlerts.toString(),
      subtitle: mode === 'global' ? 'across all markets' : `in ${marketCode}`,
      icon: <AlertBellIcon size={20} color="#ef4444" />
    },
    {
      title: "Services Healthy",
      value: `${metrics.servicesHealthy} / ${metrics.servicesTotal}`,
      subtitle: `${metrics.servicesCoverage}% coverage`,
      icon: <HealthOperationsIcon size={20} color="#2563eb" />
    }
  ];

  return (
    <div className="global-metrics-grid">
      {metricCards.map((card, index) => (
        <div
          className="global-metric-card global-metric-card--insight"
          key={`${card.title}-${index}`}
          role="button"
          tabIndex={0}
          aria-expanded={openInsight === card.title}
          aria-label={`${card.title} — show insight`}
          onClick={() =>
            setOpenInsight((prev) => (prev === card.title ? null : card.title))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpenInsight((prev) =>
                prev === card.title ? null : card.title
              );
            }
          }}
        >
          <div className="global-metric-card-header">
            <span className="global-metric-label">{card.title}</span>
            <div className="global-metric-icon">
              {card.icon}
            </div>
          </div>
          <span className="metric-insight-badge" aria-hidden="true" title="Insight available">
            ✨
          </span>

          <div className="global-value-row">
            <span className="global-metric-value">{card.value}</span>

            {card.extraValue && (
              <span className="global-extra-value">{card.extraValue}</span>
            )}

            {card.change && (
              <span className={`global-change ${card.type}`}>
                {card.change}
              </span>
            )}

            {card.trend && (
              <></>
              // <div className="global-trend-graph">
              //   <svg viewBox="0 0 60 20" preserveAspectRatio="none">
              //     <polyline
              //       points="2,18 12,10 24,11 34,3 44,8 54,1"
              //       fill="none"
              //       stroke="#68b561"
              //       strokeWidth="2"
              //       strokeLinecap="round"
              //       strokeLinejoin="round"
              //     />
              //   </svg>
              // </div>
            )}
          </div>

          <div className="global-metric-subtext">{card.subtitle}</div>

          {/* Always mounted, collapsed by class. Mounting on open would give
              the browser nothing to animate from, so the panel would appear
              at full height instantly however the CSS was written. */}
          <div
            className={`metric-insight${
              openInsight === card.title ? " is-open" : ""
            }`}
            role="status"
            aria-hidden={openInsight !== card.title}
          >
            <div className="metric-insight__inner">
              {INSIGHTS_READY ? null : (
                <>
                  <span className="metric-insight__tag">Preview</span>
                  <p>
                    AI insight for <strong>{card.title}</strong> is not
                    generated yet. This panel will explain what moved, which
                    markets or services drove it, and what to check first.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardMetrics;